import { useCallback } from 'react';
import {
  useAbortPostVideoUploadMutation,
  useCompletePostVideoUploadMutation,
  useCreatePostVideoUploadMutation,
} from '../../../../../redux/api/fymAPI';
import { PromoteMediaDraft } from '../../draftStore';
import {
  isStoppedError,
  MasterPartsStop,
  UploadedMasterPart,
  uploadMasterParts,
} from '../../../../smartLinks/catalog/song/masterUploadParts';
import { measureVideoFile, mediaDraftOfVideoUpload, pickerUploadType } from './pickerUpload';

interface SendVideoProps {
  file: File;
  /** False once the picker moved on (another upload, a cancel, a close): then nothing attaches. */
  isCurrent: () => boolean;
  /** Gives the stop of the step on the way to the caller (null when nothing can be stopped). */
  follow: (abort: (() => void) | null) => void;
  /** The percent of the bytes that S3 got. */
  onProgress: (percent: number) => void;
  /** The parts are in; the server joins, checks and measures the video. */
  onChecking: () => void;
}

/**
 * The video way of the picker upload: the presigned multipart upload of music24-api
 * (`promo/post-media/uploads`), as the song master does it.
 *
 * 1. The browser measures the video (`measureVideoFile`) and starts the upload.
 * 2. The parts go straight to S3 (`uploadMasterParts`: one at a time, the real byte progress, a
 *    failed part again up to 3 times). A stop aborts the part on the way and the upload on the
 *    server; S3 drops the parts.
 * 3. The complete call saves the video. Nothing stops it: a video that the picker no longer wants
 *    stays unused, and the server deletes it after 7 days.
 *
 * Resolves with the Composer media, or null when the picker moved on. Rejects when a step failed
 * (the upload on the server is aborted first).
 */
export function usePickerVideoSender(accountId: number) {
  const [createUpload] = useCreatePostVideoUploadMutation();
  const [completeUpload] = useCompletePostVideoUploadMutation();
  const [abortUpload] = useAbortPostVideoUploadMutation();

  return useCallback(
    async ({ file, isCurrent, follow, onProgress, onChecking }: SendVideoProps): Promise<PromoteMediaDraft | null> => {
      const measures = await measureVideoFile(file);
      if (!isCurrent()) {
        return null;
      }
      const createRequest = createUpload({
        accountId,
        fileName: file.name,
        contentType: pickerUploadType(file) ?? '',
        sizeBytes: file.size,
      });
      follow(() => createRequest.abort());
      const created = await createRequest.unwrap();
      const ids = { accountId, uploadId: created.uploadId, key: created.key };
      const abortOnServer = () => {
        abortUpload(ids).catch(console.error);
      };
      if (!isCurrent()) {
        abortOnServer();
        return null;
      }

      const stop = new MasterPartsStop();
      follow(() => {
        stop.stop();
        abortOnServer();
      });
      let lastPercent = -1;
      let parts: UploadedMasterPart[];
      try {
        parts = await uploadMasterParts({
          file,
          parts: created.parts,
          partSizeBytes: created.partSizeBytes,
          stop,
          onProgress: (sent) => {
            const percent = Math.min(100, Math.floor((sent / Math.max(file.size, 1)) * 100));
            // One state change per percent, not one per progress event.
            if (percent !== lastPercent) {
              lastPercent = percent;
              onProgress(percent);
            }
          },
        });
      } catch (failure) {
        if (isStoppedError(failure)) {
          // The stop aborted the upload on the server already.
          return null;
        }
        follow(null);
        abortOnServer();
        throw failure;
      }

      follow(null);
      if (!isCurrent()) {
        abortOnServer();
        return null;
      }
      onChecking();
      const { file: saved } = await completeUpload({ ...ids, parts, measures }).unwrap();
      if (!isCurrent()) {
        return null;
      }
      return mediaDraftOfVideoUpload({ file: saved, name: file.name, measures });
    },
    [abortUpload, accountId, completeUpload, createUpload]
  );
}
